import { useEffect, useState } from "react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { Download, X } from "lucide-react";

const STATUS_STYLES = {
  draft: "bg-neutral-100 text-neutral-700",
  pending: "bg-[#FEF7E0] text-[#B98900]",
  approved: "bg-[#E6F4EA] text-[#008060]",
  rejected: "bg-[#FCE8E6] text-[#D82C0D]",
};

export default function AdminReports() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [downloading, setDownloading] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  useEffect(() => {
    api
      .get("/timesheets/all")
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API_BASE}/timesheets/export.csv`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "approved_timesheets.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(e.message || "Failed to export");
    } finally {
      setDownloading(false);
    }
  };

  const rejectApproved = async () => {
    if (!rejectReason.trim()) {
      toast.error("Add a reason before rejecting.");
      return;
    }
    setRejecting(true);
    try {
      const { data } = await api.post(`/timesheets/${rejectTarget.id}/reject`, {
        reason: rejectReason,
      });
      setItems((prev) => prev.map((item) => (item.id === data.id ? data : item)));
      setRejectTarget(null);
      setRejectReason("");
      toast.success("Timesheet rejected and reopened for staff edits");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRejecting(false);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-[1400px]">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Reports</div>
          <h1 className="font-display font-black text-3xl md:text-5xl leading-none">
            All timesheets.
          </h1>
        </div>
        <Button
          onClick={downloadCsv}
          disabled={downloading}
          className="rounded-sm bg-[#0033CC] hover:bg-[#002299] text-white"
          data-testid="export-csv-btn"
        >
          <Download size={16} className="mr-2" />
          Export approved (CSV)
        </Button>
      </div>

      <div className="flex gap-2 mb-4">
        {["all", "pending", "approved", "rejected", "draft"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f}`}
            className={`text-xs uppercase tracking-wider px-3 py-2 rounded-sm border transition-colors ${
              filter === f
                ? "bg-[#111111] text-white border-[#111111]"
                : "bg-white border-[#E5E5E5] text-neutral-600 hover:bg-neutral-50"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F3F2]">
            <tr className="text-left text-xs uppercase tracking-wider text-neutral-500">
              <th className="px-4 py-3">Staff</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Week</th>
              <th className="px-4 py-3 text-right">Hours</th>
              <th className="px-4 py-3 text-right">Billable</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Reviewed by</th>
              {user?.is_super_admin && <th className="px-4 py-3 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={user?.is_super_admin ? 8 : 7} className="px-4 py-6 text-center text-neutral-500">
                  No timesheets to show.
                </td>
              </tr>
            ) : (
              filtered.map((r) => (
                <tr key={r.id} className="border-t border-[#E5E5E5]" data-testid={`report-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium">{r.user_name}</td>
                  <td className="px-4 py-3 text-neutral-600">{r.user_email}</td>
                  <td className="px-4 py-3 font-mono-num">{r.week_start}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{r.total_hours}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{r.billable_hours}</td>
                  <td className="px-4 py-3">
                    <span className={`status-pill ${STATUS_STYLES[r.status]}`}>{r.status}</span>
                  </td>
                  <td className="px-4 py-3 text-neutral-600">{r.reviewed_by || "—"}</td>
                  {user?.is_super_admin && (
                    <td className="px-4 py-3 text-right">
                      {r.status === "approved" && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-sm border-[#D82C0D] text-[#D82C0D] hover:bg-[#FCE8E6]"
                          onClick={() => setRejectTarget(r)}
                          data-testid={`reject-approved-btn-${r.id}`}
                        >
                          <X size={14} className="mr-1" /> Reject & reopen
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog
        open={!!rejectTarget}
        onOpenChange={(open) => {
          if (!open && !rejecting) {
            setRejectTarget(null);
            setRejectReason("");
          }
        }}
      >
        <DialogContent className="rounded-md">
          <DialogHeader>
            <DialogTitle className="font-display">Reject and reopen timesheet</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-neutral-600">
            This will return <strong>{rejectTarget?.user_name}</strong>&apos;s approved week of{" "}
            <span className="font-mono-num">{rejectTarget?.week_start}</span> to an editable state.
          </p>
          <Textarea
            placeholder="Reason for rejection…"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            data-testid="reject-approved-reason-input"
            className="rounded-sm"
            rows={4}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRejectTarget(null);
                setRejectReason("");
              }}
              disabled={rejecting}
              className="rounded-sm"
              data-testid="cancel-reject-approved-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={rejectApproved}
              disabled={rejecting}
              className="rounded-sm bg-[#D82C0D] hover:bg-[#B32309] text-white"
              data-testid="confirm-reject-approved-btn"
            >
              Reject & reopen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
